import React from 'react';
import { Profile } from '@/types/auth';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Users, UserPlus, Crown, Heart } from 'lucide-react';

interface StatsWidgetProps {
  profile: Profile;
}

export const StatsWidget: React.FC<StatsWidgetProps> = ({ profile }) => {
  // Fetch follower/following counts
  const { data: followStats } = useQuery({
    queryKey: ['follow-stats', profile.id],
    queryFn: async () => {
      const [followersResult, followingResult] = await Promise.all([
        supabase
          .from('followers')
          .select('id', { count: 'exact' })
          .eq('following_id', profile.id),
        supabase
          .from('followers')
          .select('id', { count: 'exact' })
          .eq('follower_id', profile.id),
      ]);

      return {
        followers: followersResult.count || 0,
        following: followingResult.count || 0,
      };
    },
  });

  // Fetch bulletin likes count
  const { data: likesCount } = useQuery({
    queryKey: ['likes-count', profile.id],
    queryFn: async () => {
      const { data: bulletins } = await supabase
        .from('bulletins')
        .select('like_count')
        .eq('user_id', profile.id);

      return bulletins?.reduce((total, bulletin) => total + (bulletin.like_count || 0), 0) || 0;
    },
  });

  const stats = [
    {
      label: 'Followers',
      value: followStats?.followers || 0,
      icon: Users,
      color: 'text-blue-500',
    },
    {
      label: 'Following',
      value: followStats?.following || 0,
      icon: UserPlus,
      color: 'text-green-500',
    },
    {
      label: 'ELO Rating',
      value: profile.elo_rating || 0,
      icon: Crown,
      color: 'text-yellow-500',
    },
    {
      label: 'Total Likes',
      value: likesCount || 0,
      icon: Heart,
      color: 'text-red-500',
    },
  ];

  return (
    <div className="h-full">
      <h3 className="text-sm font-medium text-muted-foreground mb-4">Stats</h3>
      
      <div className="grid grid-cols-2 gap-4 h-[calc(100%-2rem)]">
        {stats.map((stat) => (
          <div key={stat.label} className="text-center">
            <div className="flex items-center justify-center mb-2">
              <stat.icon className={`h-5 w-5 ${stat.color}`} />
            </div>
            <div className="text-lg font-bold">{stat.value.toLocaleString()}</div>
            <div className="text-xs text-muted-foreground">{stat.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
};